/**
 * Terminal 82 - ETH Data Service
 *
 * Uses static historical data + live API for recent days.
 * Static data: 3776 days of ETH history from CryptoCompare (Aug 2015 - Dec 2025)
 */

import type { DayData } from '../types';
import { calculateDayId } from '../card';
import ethHistoryData from '../data/eth-daily-history.json';

// Legacy CryptoCompare API (for intraday data)
const BASE_URL = 'https://min-api.cryptocompare.com/data/v2';

// Proxy URL for API requests (keeps API key server-side, bypasses CORS)
// Development: local Express server, Production: Vercel serverless function
const isDev = import.meta.env.DEV;
const DEV_PROXY_URL = import.meta.env.VITE_EXPORT_SERVER_URL || 'http://localhost:3001';

function getProxyUrl(limit: number): string {
  if (isDev) {
    return `${DEV_PROXY_URL}/api/eth/daily?limit=${limit}`;
  }
  // Production: Vercel serverless function
  return `/api/eth-daily?limit=${limit}`;
}

// Cache for historical data (excluding today) - refreshes daily
let historicalCache: { data: DayData[]; timestamp: number } | null = null;
const HISTORICAL_CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours

// Cache for today's live candle - refreshes every 5 min
let todayCache: { data: DayData; timestamp: number } | null = null;
const TODAY_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

// Static data cache
interface StaticHistoryData {
  generated: string;
  symbol: string;
  source: string;
  totalDays: number;
  firstDate: string;
  lastDate: string;
  data: Array<{
    date: string;
    open: number;
    high: number;
    low: number;
    close: number;
    volume: number;
    changePercent: number;
  }>;
}

const staticHistory = ethHistoryData as StaticHistoryData;

// ============================================
// Types
// ============================================

export interface OHLCDataPoint {
  timestamp: number;
  open: number;
  high: number;
  low: number;
  close: number;
}

interface CryptoCompareDay {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volumefrom: number;
  volumeto: number;
}

interface CryptoCompareResponse {
  Response: string;
  Message?: string;
  Data: {
    Data: CryptoCompareDay[];
  };
}

// ============================================
// API Functions
// ============================================

/**
 * Fetch daily OHLC data for ETH from CryptoCompare
 * Returns accurate daily candles
 */
export async function fetchOHLCData(days: number = 365): Promise<OHLCDataPoint[]> {
  const url = `${BASE_URL}/histoday?fsym=ETH&tsym=USD&limit=${days}`;

  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`CryptoCompare API error: ${response.status}`);
  }

  const json: CryptoCompareResponse = await response.json();

  if (json.Response === 'Error') {
    throw new Error(json.Message || 'CryptoCompare API error');
  }

  return json.Data.Data.map((d) => ({
    timestamp: d.time * 1000, // Convert to milliseconds
    open: d.open,
    high: d.high,
    low: d.low,
    close: d.close,
  }));
}

/**
 * Convert OHLC data point to DayData
 */
export function ohlcToDayData(
  ohlc: OHLCDataPoint,
  mcap: number = 0,
  volume: number = 0
): DayData {
  const date = new Date(ohlc.timestamp);
  const dateStr = date.toISOString().split('T')[0];

  const changePercent = ((ohlc.close - ohlc.open) / ohlc.open) * 100;

  return {
    date: dateStr,
    dayId: calculateDayId(dateStr),
    ohlc: {
      open: ohlc.open,
      high: ohlc.high,
      low: ohlc.low,
      close: ohlc.close,
    },
    change: {
      percent: parseFloat(changePercent.toFixed(2)),
      absolute: parseFloat((ohlc.close - ohlc.open).toFixed(2)),
    },
    market: {
      mcap,
      volume,
    },
    intraday: [],
    intradayVolumes: [],
  };
}

/**
 * Convert static data entry to DayData
 */
function staticToDayData(entry: StaticHistoryData['data'][0]): DayData {
  return {
    date: entry.date,
    dayId: calculateDayId(entry.date),
    ohlc: {
      open: entry.open,
      high: entry.high,
      low: entry.low,
      close: entry.close,
    },
    change: {
      percent: entry.changePercent,
      absolute: parseFloat((entry.close - entry.open).toFixed(2)),
    },
    market: {
      mcap: 0,
      volume: entry.volume,
    },
    intraday: [],
    intradayVolumes: [],
  };
}

/**
 * Get today's date string (UTC)
 */
function getTodayDateStr(): string {
  return new Date().toISOString().split('T')[0];
}

/**
 * Fetch today's live candle data (5-min cache)
 */
async function fetchTodayData(): Promise<DayData | null> {
  const todayStr = getTodayDateStr();

  // Check today's cache
  if (todayCache && Date.now() - todayCache.timestamp < TODAY_CACHE_TTL) {
    console.log('Using cached today data');
    return todayCache.data;
  }

  try {
    // Use proxy to bypass CORS (browser can't send x-api-key header directly)
    const url = getProxyUrl(1);

    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Proxy API error: ${response.status}`);
    }

    const json = await response.json();

    if (json.error) {
      throw new Error(json.error);
    }

    if (json.Err && Object.keys(json.Err).length > 0) {
      throw new Error(JSON.stringify(json.Err));
    }

    if (!json.Data || json.Data.length === 0) {
      return null;
    }

    const d = json.Data[0];
    const date = new Date(d.TIMESTAMP * 1000).toISOString().split('T')[0];

    // Only use if it's actually today
    if (date !== todayStr) {
      console.log('API returned data for', date, 'not today', todayStr);
      return null;
    }

    const changePercent = d.OPEN > 0 ? ((d.CLOSE - d.OPEN) / d.OPEN) * 100 : 0;

    const todayData: DayData = {
      date,
      dayId: calculateDayId(date),
      ohlc: {
        open: d.OPEN,
        high: d.HIGH,
        low: d.LOW,
        close: d.CLOSE,
      },
      change: {
        percent: parseFloat(changePercent.toFixed(2)),
        absolute: parseFloat((d.CLOSE - d.OPEN).toFixed(2)),
      },
      market: {
        mcap: 0,
        volume: d.QUOTE_VOLUME || 0,
      },
      intraday: [],
      intradayVolumes: [],
    };

    // Update cache
    todayCache = { data: todayData, timestamp: Date.now() };
    console.log('Fetched live today data:', todayData.date, todayData.ohlc);

    return todayData;
  } catch (err) {
    console.warn('Failed to fetch today data:', err);
    return null;
  }
}

/**
 * Fetch historical data (excluding today) from static + API
 * Uses daily cache for any API-fetched historical data
 */
async function fetchHistoricalOnly(): Promise<DayData[]> {
  const todayStr = getTodayDateStr();

  // Use static data as base (excluding today if present)
  const staticData = staticHistory.data
    .filter(d => d.date < todayStr)
    .map(staticToDayData);

  const lastStaticDate = staticHistory.lastDate;
  const yesterdayDate = new Date(Date.now() - 86400000).toISOString().split('T')[0];

  // If static data covers up to yesterday, we're good
  if (lastStaticDate >= yesterdayDate) {
    return staticData;
  }

  // Check historical cache
  if (historicalCache && Date.now() - historicalCache.timestamp < HISTORICAL_CACHE_TTL) {
    console.log('Using cached historical data');
    return historicalCache.data;
  }

  // Need to fetch missing days between static and yesterday
  // Use proxy to bypass CORS
  try {
    const daysSinceStatic = Math.ceil(
      (new Date(yesterdayDate).getTime() - new Date(lastStaticDate).getTime()) / 86400000
    );

    const url = getProxyUrl(daysSinceStatic + 5);

    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Proxy API error: ${response.status}`);
    }

    const json = await response.json();

    if (json.error) {
      throw new Error(json.error);
    }

    if (json.Err && Object.keys(json.Err).length > 0) {
      throw new Error(JSON.stringify(json.Err));
    }

    const freshDays: DayData[] = json.Data
      .map((d: any) => {
        const date = new Date(d.TIMESTAMP * 1000).toISOString().split('T')[0];
        const changePercent = d.OPEN > 0 ? ((d.CLOSE - d.OPEN) / d.OPEN) * 100 : 0;
        return {
          date,
          dayId: calculateDayId(date),
          ohlc: { open: d.OPEN, high: d.HIGH, low: d.LOW, close: d.CLOSE },
          change: {
            percent: parseFloat(changePercent.toFixed(2)),
            absolute: parseFloat((d.CLOSE - d.OPEN).toFixed(2)),
          },
          market: { mcap: 0, volume: d.QUOTE_VOLUME || 0 },
          intraday: [],
          intradayVolumes: [],
        };
      })
      .filter((d: DayData) => d.date > lastStaticDate && d.date < todayStr);

    const allHistorical = [...staticData, ...freshDays].sort((a, b) =>
      a.date.localeCompare(b.date)
    );

    // Update cache
    historicalCache = { data: allHistorical, timestamp: Date.now() };
    console.log('Fetched historical data, total days:', allHistorical.length);

    return allHistorical;
  } catch (err) {
    console.warn('Failed to fetch historical data:', err);
    return staticData;
  }
}

/**
 * Fetch ALL historical data + today's live candle
 * - Static: bundled JSON (updates with builds)
 * - Fresh: API for days after static data (24h cache)
 * - Today: live API (5-min cache)
 */
export async function fetchHistoricalData(): Promise<DayData[]> {
  // Get historical data (excluding today)
  const historical = await fetchHistoricalOnly();

  // Get today's live data
  const today = await fetchTodayData();

  // Combine: historical + today (if available)
  return today ? [...historical, today] : historical;
}

/**
 * Fetch ONLY from static data file - no live data
 * Use this for NFT exports to ensure data consistency
 */
export async function fetchStaticHistoricalData(): Promise<DayData[]> {
  return staticHistory.data.map(staticToDayData);
}

/**
 * Fetch data for a specific date
 * - For today: fetch live data (5-min cache)
 * - For past dates: use static/historical data
 */
export async function fetchDayData(date: string): Promise<DayData> {
  const todayStr = getTodayDateStr();

  // For today's date, use live data
  if (date === todayStr) {
    const todayData = await fetchTodayData();
    if (todayData) {
      return todayData;
    }
    // Fall through to check static data if live fetch failed
  }

  // Check static data
  const staticEntry = staticHistory.data.find(d => d.date === date);
  if (staticEntry) {
    return staticToDayData(staticEntry);
  }

  // For dates not in static, try historical cache or fetch
  const historical = await fetchHistoricalOnly();
  const dayData = historical.find(d => d.date === date);
  if (dayData) {
    return dayData;
  }

  throw new Error(`No data available for ${date}`);
}

/**
 * Get total days of historical data available
 */
export function getTotalHistoricalDays(): number {
  return staticHistory.totalDays;
}

/**
 * Get date range of historical data
 */
export function getHistoricalDateRange(): { first: string; last: string } {
  return {
    first: staticHistory.firstDate,
    last: staticHistory.lastDate,
  };
}

/**
 * Intraday data structure with prices and volumes
 */
export interface IntradayData {
  prices: number[];   // 24 hourly close prices
  volumes: number[];  // 24 hourly volumes
}

// Intraday cache to avoid refetching same day's hourly data
const intradayCache = new Map<string, IntradayData>();

/**
 * Fetch hourly data for a specific day (24 hours)
 * Used for intraday chart on cards
 * Returns empty arrays if no real data available - NO FAKE DATA
 * Results are cached to avoid refetching the same day
 */
export async function fetchIntradayData(date: string): Promise<IntradayData> {
  // Check cache first
  const cached = intradayCache.get(date);
  if (cached) {
    return cached;
  }

  // Parse date to get timestamp for start of day (UTC)
  const dayStart = new Date(date + 'T00:00:00Z');
  const toTimestamp = Math.floor(dayStart.getTime() / 1000) + 86400; // End of day

  const url = `${BASE_URL}/histohour?fsym=ETH&tsym=USD&limit=24&toTs=${toTimestamp}`;

  try {
    const response = await fetch(url);
    if (!response.ok) {
      console.warn(`No hourly data available for ${date}`);
      const empty = { prices: [], volumes: [] };
      intradayCache.set(date, empty);
      return empty;
    }

    const json = await response.json();

    if (json.Response === 'Error' || !json.Data?.Data) {
      console.warn(`No hourly data available for ${date}`);
      const empty = { prices: [], volumes: [] };
      intradayCache.set(date, empty);
      return empty;
    }

    // Extract close prices and volumes for each hour
    // Skip first element - CryptoCompare returns 25 hours, first is previous day's last hour
    const hourlyData = json.Data.Data.slice(1);
    const prices = hourlyData.map((h: { close: number }) => h.close);
    const volumes = hourlyData.map((h: { volumeto: number }) => h.volumeto);

    // Validate we got real data (not all zeros)
    if (prices.every((p: number) => p === 0)) {
      console.warn(`Hourly data for ${date} contains only zeros`);
      const empty = { prices: [], volumes: [] };
      intradayCache.set(date, empty);
      return empty;
    }

    const result = { prices, volumes };
    intradayCache.set(date, result);
    return result;
  } catch (err) {
    console.warn(`Failed to fetch hourly data for ${date}:`, err);
    const empty = { prices: [], volumes: [] };
    intradayCache.set(date, empty);
    return empty;
  }
}

/**
 * Fetch day data with intraday prices and volumes populated
 */
export async function fetchDayDataWithIntraday(date: string): Promise<DayData> {
  const dayData = await fetchDayData(date);
  const intradayData = await fetchIntradayData(date);

  return {
    ...dayData,
    intraday: intradayData.prices,
    intradayVolumes: intradayData.volumes,
  };
}
